import React, { useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import BasicDrawer from "@components/drawer/BasicDrawer";
import useAxios from "@libs/axios/useAxios";
import { GET_PRODUCT_INVENTORY_URL } from "@libs/apiUrl";
import useIntersection from "@hook/useIntersection";
import ColorBall from "@components/item/ColorBall";
import { Flex } from "antd";

const RegisterList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openAccordion, setOpenAccordion] = useState(null as number | null);
  const [selectedProduct, setSelectedProduct] = useState<{
    productId: number;
    status: string;
  } | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { axiosInstance } = useAxios();
  const { getProductStock, getInventorySortCode } = useApiRequest();

  const categoryPathsStr = searchParams.get("category_paths") || "";

  const categoryPaths = categoryPathsStr
    ? decodeURIComponent(categoryPathsStr)
        .split("|")
        .map((str) => str.trim())
    : [];

  const { data: inventorySortList } = useQuery({
    queryKey: ["inventorySortCode"],
    queryFn: getInventorySortCode,
  });

  const filters = useMemo(
    () => ({
      product_name: searchParams.get("product_name") ?? "",
      status: searchParams.get("status") ?? "",
      stock_from: searchParams.get("stock_from") ?? "",
      stock_to: searchParams.get("stock_to") ?? "",
      category: searchParams.get("category") ?? "",
      order_by: searchParams.get("order_by") ?? "DATE_DESC",
      status_labels: searchParams.get("status_labels") ?? "",
    }),
    [searchParams],
  );

  // 필터값 여부
  const { order_by, ...filterWithoutOrderBy } = filters;
  const hasFilterValue = Object.values(filterWithoutOrderBy).some(
    (value) => value !== "",
  );

  // draft_status → 프론트 status 코드 변환
  const mapDraftStatusToStatus = (draftStatus: string): string => {
    switch (draftStatus) {
      case "draft":
        return "STOCK_TEMP";
      case "confirmed":
        return "STOCK";
      case "sale_draft":
        return "SALE_TEMP";
      case "sale_confirmed":
        return "SALE";
      default:
        return draftStatus;
    }
  };

  const handleDetailClick = (
    productId: number,
    status: string,
    index: number,
  ) => {
    const isSameProductOpened = openAccordion === index;

    setOpenAccordion(isSameProductOpened ? null : index);

    if (!isSameProductOpened) {
      setSelectedProduct({ productId, status });
    }
  };

  const { data: productStock } = useQuery({
    queryKey: [
      "productStock",
      selectedProduct?.productId,
      selectedProduct?.status,
    ],
    queryFn: () =>
      getProductStock(selectedProduct!.productId, selectedProduct!.status),
    enabled: !!selectedProduct?.productId && !!selectedProduct?.status,
  });

  const getProductInventory = useCallback(
    async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam - 1,
        size: 10,
        product_name: filters.product_name,
        status: filters.status,
        stock_from: filters.stock_from,
        stock_to: filters.stock_to,
        category: filters.category,
        order_by: filters.order_by,
      };

      const searchParams = new URLSearchParams(
        Object.entries(params)
          .filter(([_, value]) => value !== "")
          .map(([key, value]) => [key, String(value)]),
      );

      const res = await axiosInstance.get(
        `${GET_PRODUCT_INVENTORY_URL}?${searchParams}`,
      );
      return res.status === 200 ? res.data : null;
    },
    [axiosInstance, filters],
  );

  const {
    data: productList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["productInventory", filters],
    queryFn: getProductInventory,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.data?.total_count || 0;
      const loadedCount = allPages.reduce(
        (sum, page) => sum + (page?.data?.list?.length || 0),
        0,
      );

      // 다음 페이지가 있는지 판단
      return loadedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  const updateFilter = useCallback(
    (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams);

      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }

      // 필터 변경시 첫 페이지로 리셋
      if (key !== "page") {
        newParams.set("page", "1");
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // 정렬 변경 핸들러
  const handleSortChange = (sortValue: string) => {
    updateFilter("order_by", sortValue);
    setIsOpen(false);
  };

  const renderStatusIcon = (status: any) => {
    switch (status) {
      case "SALE":
        return (
          <StatusIconWrapper>
            <Image
              src={theme.image.check_success_icon.default}
              width="1.8rem"
              height="1.8rem"
            />
          </StatusIconWrapper>
        );
      case "STOCK_TEMP":
      case "SALE_TEMP":
        return (
          <StatusIconWrapper>
            <Image
              src={theme.image.danger_circle.default}
              width="1.8rem"
              height="1.8rem"
            />
          </StatusIconWrapper>
        );
      default:
        return null;
    }
  };

  // 재고 개수에 따른 상태 및 색상 표시 함수
  const renderStockIndicator = (stock: number) => {
    let iconSrc;
    let color;
    let bgColor;

    // 재고 개수에 따라 상태 결정
    if (stock >= 50) {
      iconSrc = theme.image.red_tag.default;
      color = theme.color.orange;
      bgColor = "rgba(255, 72, 8, 0.10)";
    } else if (stock >= 30 && stock <= 49) {
      iconSrc = theme.image.yellow_tag.default;
      color = theme.color.tag_yellow;
      bgColor = "rgba(226, 185, 18, 0.10)";
    } else {
      iconSrc = theme.image.green_tag.default;
      color = theme.color.green;
      bgColor = theme.color.light_green;
    }

    return (
      <StockIndicator color={color} bgColor={bgColor}>
        <Image src={iconSrc} width="1.8rem" height="1.8rem" />
        <div>{stock}</div>
      </StockIndicator>
    );
  };

  return (
    <Container>
      <InputWrapper>
        <IconBox
          onClick={() => navigate("/search", { state: { from: "register" } })}>
          <Image
            src={theme.image.search_icon.default}
            width="2.5rem"
            height="2.5rem"
          />
          <Name>
            {filters.product_name.length < 1 &&
              filters.status.length < 1 &&
              filters.stock_from.length < 1 &&
              filters.stock_to.length < 1 &&
              categoryPaths.length < 1 && <span>상품 검색</span>}
            {filters.product_name && filters.product_name !== ""
              ? `${filters.product_name},`
              : ""}
            {filters.status_labels && filters.status_labels !== ""
              ? ` ${filters.status_labels},`
              : ""}

            {filters.stock_to || filters.stock_from !== ""
              ? `${filters.stock_from} ~ ${filters.stock_to},`
              : ""}
            {categoryPaths.length > 0 ? `${categoryPaths.join(" , ")},` : ""}
          </Name>
        </IconBox>

        {hasFilterValue && (
          <DeleteButton
            onClick={() => navigate("/register-list", { replace: true })}>
            <Image
              src={theme.image.close.default}
              height="2.4rem"
              width="2.4rem"
            />
          </DeleteButton>
        )}
      </InputWrapper>

      <FilterWrapper>
        <TotalProduct>
          총 {productList?.pages[0]?.data?.total_count}건
        </TotalProduct>

        <SortButton onClick={() => setIsOpen(true)}>
          <span>
            {filters.order_by
              ? inventorySortList?.find(
                  (item: any) => item.code === filters.order_by,
                )?.name_ko
              : "전체"}
          </span>
          <Image
            src={theme.image.arrow_down.default}
            width="1.6rem"
            height="1.6rem"
          />
        </SortButton>

        <BasicDrawer open={isOpen} setOpen={setIsOpen} padding="0rem">
          <SortList>
            {inventorySortList?.map((sort: any) => (
              <SortItem
                key={sort.code}
                isSelected={filters.order_by === sort.code} // filters 사용!
                onClick={() => handleSortChange(sort.code)}>
                <span>{sort.name_ko}</span>
                {filters.status === sort.code && (
                  <Image
                    src={theme.image.check_green.default}
                    width="2rem"
                    height="2rem"
                  />
                )}
              </SortItem>
            ))}
          </SortList>
        </BasicDrawer>
      </FilterWrapper>

      {productList?.pages.map((page) =>
        page.data?.list.map((product: any, index: number) => (
          <ProductItem
            key={product.id}
            onClick={() =>
              handleDetailClick(product.product_id, mapDraftStatusToStatus(product.draft_status), index)
            }>
            <ProductImageWrapper>
              <ProductImage>
                <Image src={product.thumbnail_url} width="5rem" height="5rem" />
              </ProductImage>
              {renderStatusIcon(mapDraftStatusToStatus(product.draft_status))}
            </ProductImageWrapper>

            <Flex
              style={{ flexDirection: "column", width: "100%" }}
              gap={"1rem"}>
              <ProductHeader>
                <ProductInfo>
                  <ProductName>
                    {product.name_ko ? product.name_ko : "-"}
                  </ProductName>
                  <ProductCategory>{product.category_ko}</ProductCategory>
                </ProductInfo>

                <StockInfo>
                  {renderStockIndicator(product.total_stock)}
                </StockInfo>
              </ProductHeader>

              {openAccordion === index &&
                selectedProduct?.productId === product.product_id &&
                selectedProduct?.status === mapDraftStatusToStatus(product.draft_status) &&
                productStock &&
                Array.isArray(productStock) &&
                productStock.length > 0 && (
                  <ProductDetails>
                    {Object.values(
                      productStock.reduce((acc: any, item: any) => {
                        const colorId = item.color_id;
                        if (!acc[colorId]) {
                          acc[colorId] = {
                            color_id: colorId,
                            color_name: item.color_name,
                            color_hex_code: item.color_hex_code,
                            sizes: [],
                            total_amount: 0,
                          };
                        }
                        acc[colorId].sizes.push({
                          size_name: item.size_name,
                          amount: item.amount,
                        });
                        acc[colorId].total_amount += item.amount;
                        return acc;
                      }, {}),
                    ).map((colorGroup: any) => (
                      <ColorGroup key={colorGroup.color_id}>
                        <ColorRow>
                          <ColorBall
                            color={colorGroup.color_hex_code}
                            colorName={colorGroup.color_name}
                          />
                          <ColorCount>{colorGroup.total_amount}</ColorCount>
                        </ColorRow>
                        <SizeList>
                          {colorGroup.sizes.map(
                            (size: any, sizeIndex: number) => (
                              <SizeRow key={sizeIndex}>
                                <SizeName>{size.size_name}</SizeName>
                                <SizeAmount>{size.amount}개</SizeAmount>
                              </SizeRow>
                            ),
                          )}
                        </SizeList>
                      </ColorGroup>
                    ))}
                  </ProductDetails>
                )}

              <DetailButton
                onClick={() =>
                  navigate(
                    `/product-detail/${product.product_id}/${mapDraftStatusToStatus(product.draft_status)}`,
                  )
                }>
                <span>상세보기</span>
                <Image
                  src={theme.image.search_icon.default}
                  width="1.6rem"
                  height="1.6rem"
                />
              </DetailButton>
            </Flex>
          </ProductItem>
        )),
      )}

      {hasNextPage && <div ref={setObserverTarget} />}
    </Container>
  );
};

export default RegisterList;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "1rem 2rem",
});

const ProductItem = styled.div({
  background: "white",
  borderRadius: "1rem",
  overflow: "hidden",
  boxShadow: "0px 4px 30px 0px #EDEDED",
  display: "flex",
  gap: "1rem",
  padding: "1rem",
});

const ProductHeader = styled.div({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
});

const ProductImageWrapper = styled.div({
  position: "relative",
  height: "100%",
});

const ProductImage = styled.div({
  borderRadius: "0.5rem",
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  width: "5rem",
  height: "5rem",

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const StatusIconWrapper = styled.div({
  position: "absolute",
  bottom: "-0.3rem",
  right: "-0.1rem",
  zIndex: 2,
});

const ProductInfo = styled.div({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
});

const ProductName = styled.div(({ theme }) => ({
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.bold,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "20rem",
}));

const ProductCategory = styled.div({
  fontSize: "1.4rem",
});

const StockInfo = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "1rem",
});

const StockIndicator = styled.div(
  ({ color, theme, bgColor }: { color: any; theme?: any; bgColor: any }) => ({
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.bold,
    color,
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    background: bgColor,
    borderRadius: "100px",
    padding: "0.5rem 0.7rem",
  }),
);

const ProductDetails = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const ColorRow = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  fontSize: "1.2rem",
  justifyContent: "space-between",
  color: theme.color.gray,
}));

const ColorCount = styled.div({
  fontWeight: 500,
});

const ColorGroup = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
});

const SizeList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.2rem",
  paddingLeft: "2.4rem",
});

const SizeRow = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "1.2rem",
  color: theme.color.gray,
}));

const SizeName = styled.span({
  fontSize: "1.1rem",
});

const SizeAmount = styled.span({
  fontSize: "1.1rem",
  fontWeight: 500,
});

const DetailButton = styled.button({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  border: "none",
  background: "none",
  cursor: "pointer",
  fontSize: "1.4rem",
  color: "#888",

  "& span": {
    marginRight: "0.5rem",
  },
});

const InputWrapper = styled.div(({ theme }) => ({
  width: "100%",
  height: "6rem",
  padding: "1rem 2rem 1rem 2rem",
  fontSize: "1.6rem",
  borderRadius: "10px",
  lineHeight: "1.6rem",
  fontWeight: theme.fontWeight.medium,
  backgroundColor: theme.color.border_gray,
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  position: "relative",
  cursor: "pointer",
  color: theme.color.gray,
}));

const FilterWrapper = styled.div({
  display: "flex",
  justifyContent: "space-between",
  padding: "2rem 0",
});

const TotalProduct = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
  fontWeight: theme.fontWeight.medium,
}));

const IconBox = styled.div({
  position: "absolute",
  top: "50%",
  left: "2rem",
  transform: "translate(0,-50%)",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  cursor: "pointer",
  overflow: "hidden",
  width: "100%",
});

const Name = styled.div(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
  paddingRight: "6rem", // DeleteButton 버튼 너비 + 여유 공간 확보
  fontSize: "1.6rem",
}));

const DeleteButton = styled.button({
  position: "absolute",
  right: "2rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  cursor: "pointer",
});

const SortButton = styled.button(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  fontSize: "1.4rem",
  cursor: "pointer",
}));

const SortList = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const SortItem = styled.button<{ isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    width: "100%",
    color: isSelected ? theme.color.green : theme.color.black,
    backgroundColor: "transparent",
    borderBottom: `1px solid ${theme.color.border_gray}`,
    cursor: "pointer",
    minHeight: "5.6rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "&:last-child": {
      border: "none",
    },
  }),
);
